import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getHours, getMinutes, setHours, setMinutes } from "date-fns";
import { CalendarDays, Clock, Info } from "lucide-react";
import ToolTip from "../../../../ToolTip/ToolTip";
import { CopyableInput } from "../CopyableInput";
import { CalendarPanel, TimeSelector } from "./components";
import {
  formatDisplayValue,
  formatPickerValue,
  getDefaultPlaceholder,
  parsePickerValue,
} from "../../utils";
import { DateTimePickerProps } from "../../interfaces";

/** Popover width and the gap it keeps from the field. */
const PANEL_WIDTH = 296 as const;
const PANEL_MARGIN = 4 as const;

/**
 * Rough panel heights per mode. They only seed the first placement pass: once
 * the panel is on screen its measured height takes over.
 */
const PANEL_HEIGHTS: Record<string, number> = {
  datetime: 420,
  date: 340,
  time: 170,
};

interface PanelPosition {
  left: number;
  top: number;
}

/**
 * Date, time and date-time field with the dashboard's own calendar.
 *
 * The native `date` / `time` / `datetime-local` controls are painted by each
 * browser in its own way, which is why the value used to render differently —
 * and, on Firefox, twice — across engines. This picker renders the value and
 * the calendar itself, so every screen looks the same everywhere, while the
 * string it emits stays exactly the one the native inputs emitted.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = (props) => {
  const {
    className,
    disabled,
    error,
    isCopyable,
    isRequired,
    label,
    mode,
    name,
    onChange,
    placeholder,
    tooltipText,
    value,
  } = props;

  const selectedDate: Date | null = useMemo(
    () => parsePickerValue(value, mode),
    [value, mode],
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  // The panel edits a draft so the form only sees the value once it is applied.
  const [draftDate, setDraftDate] = useState<Date | null>(selectedDate);
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    selectedDate ?? new Date(),
  );
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const showsCalendar: boolean = mode !== "time";
  const showsTime: boolean = mode !== "date";

  /**
   * The panel is portalled to the body and positioned from the trigger's rect:
   * most of these fields live inside scrolling modals, where a panel positioned
   * inside the form would be clipped by the modal's own overflow.
   */
  const updatePanelPosition = useCallback((): void => {
    const trigger: HTMLButtonElement | null = triggerRef.current;
    if (!trigger) {
      return;
    }

    const rect: DOMRect = trigger.getBoundingClientRect();
    // Measured once the panel exists, so a mode whose real height differs from
    // the estimate still lands in the right place.
    const panelHeight: number =
      panelRef.current?.offsetHeight || PANEL_HEIGHTS[mode];

    const spaceBelow: number = window.innerHeight - rect.bottom - PANEL_MARGIN;
    const spaceAbove: number = rect.top - PANEL_MARGIN;
    const opensUpwards: boolean =
      spaceBelow < panelHeight && spaceAbove > spaceBelow;

    const preferredTop: number = opensUpwards
      ? rect.top - panelHeight - PANEL_MARGIN
      : rect.bottom + PANEL_MARGIN;

    setPanelPosition((previousPosition: PanelPosition | null) => {
      // Clamped on both axes so a field near an edge — or one in a short
      // viewport where neither side fits — still shows the whole panel.
      const nextPosition: PanelPosition = {
        left: Math.max(
          PANEL_MARGIN,
          Math.min(rect.left, window.innerWidth - PANEL_WIDTH - PANEL_MARGIN),
        ),
        top: Math.max(
          PANEL_MARGIN,
          Math.min(
            preferredTop,
            window.innerHeight - panelHeight - PANEL_MARGIN,
          ),
        ),
      };

      // Bail out when nothing moved: this runs again once the panel is
      // measured, and a fresh object every time would loop.
      if (
        previousPosition &&
        previousPosition.left === nextPosition.left &&
        previousPosition.top === nextPosition.top
      ) {
        return previousPosition;
      }
      return nextPosition;
    });
  }, [mode]);

  const openPanel = (): void => {
    if (disabled) {
      return;
    }
    setDraftDate(selectedDate);
    setVisibleMonth(selectedDate ?? new Date());
    setIsOpen(true);
  };

  const closePanel = useCallback((): void => setIsOpen(false), []);

  const applyDraft = (): void => {
    // An empty calendar with a time typed in still needs a day to anchor to.
    const appliedDate: Date = draftDate ?? new Date();
    onChange?.(formatPickerValue(appliedDate, mode));
    closePanel();
  };

  const handleSelectDate = (date: Date): void => {
    // Picking a day must not discard a time the user already set.
    const previousDate: Date | null = draftDate;
    const nextDate: Date = previousDate
      ? setMinutes(
          setHours(date, getHours(previousDate)),
          getMinutes(previousDate),
        )
      : date;
    setDraftDate(nextDate);

    if (mode === "date") {
      onChange?.(formatPickerValue(nextDate, mode));
      closePanel();
    }
  };

  const handleTimeChange = (hours: number, minutes: number): void =>
    setDraftDate(setMinutes(setHours(draftDate ?? new Date(), hours), minutes));

  // Runs twice on open: first with the estimated height to place the panel, then
  // again once `panelPosition` has mounted it and its real height can be read.
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    updatePanelPosition();
  }, [isOpen, panelPosition, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      closePanel();
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    // `true` so the panel follows the field while a modal body scrolls.
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closePanel, updatePanelPosition]);

  const displayValue: string = selectedDate
    ? formatDisplayValue(selectedDate, mode)
    : "";
  const FieldIcon = mode === "time" ? Clock : CalendarDays;

  if (isCopyable) {
    return (
      <CopyableInput
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
        value={displayValue}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-normal text-gray-500">
            {label}{" "}
            {isRequired && (
              <span className="text-xs font-normal text-gray-500">*</span>
            )}
          </label>
          {tooltipText && (
            <ToolTip text={tooltipText}>
              <Info className="h-3 w-3 shrink-0 text-gray-500" />
            </ToolTip>
          )}
        </div>
      )}

      <button
        ref={triggerRef}
        type="button"
        name={name}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? closePanel() : openPanel())}
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm disabled:bg-gray-300/20 disabled:text-gray-500 disabled:opacity-100 ${
          isOpen ? "border-primary" : "border-gray-200"
        } ${className ? className : ""}`}
      >
        <span
          className={`truncate ${displayValue ? "text-ink-strong" : "text-gray-500"}`}
        >
          {displayValue || placeholder || getDefaultPlaceholder(mode)}
        </span>
        <FieldIcon
          className={`h-4 w-4 shrink-0 ${isOpen ? "text-primary" : "text-gray-400"}`}
        />
      </button>

      {isOpen &&
        panelPosition &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={label || "Selector de fecha"}
            style={{
              left: panelPosition.left,
              top: panelPosition.top,
              width: PANEL_WIDTH,
            }}
            className="fixed z-[1100] flex flex-col gap-3 rounded-lg border border-line bg-surface p-3 shadow-modal"
          >
            {showsCalendar && (
              <CalendarPanel
                selectedDate={draftDate}
                onSelectDate={handleSelectDate}
                visibleMonth={visibleMonth}
                onVisibleMonthChange={setVisibleMonth}
              />
            )}

            {showsTime && (
              <div
                className={`flex flex-col gap-2 ${
                  showsCalendar ? "border-t border-line pt-3" : ""
                }`}
              >
                <span className="text-xs font-medium text-ink-muted">Hora</span>
                <TimeSelector
                  hours={draftDate ? getHours(draftDate) : 0}
                  minutes={draftDate ? getMinutes(draftDate) : 0}
                  onChange={handleTimeChange}
                />
              </div>
            )}

            {showsTime && (
              <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
                <button
                  type="button"
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-surface-sunken"
                  onClick={closePanel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-hover"
                  onClick={applyDraft}
                >
                  Aplicar
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}

      {error && <label className="text-xs text-red-500">{error}</label>}
    </div>
  );
};
